import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { BookOpen, FileText, Send, Clock, Check, X, Trash2 } from 'lucide-react';
import LeitorPDF from './LeitorPDF'; // PLENARIUM:LEITOR_PDF

// =====================================================================
// PLENARIUM:ATA_RETIFICACAO -- tela "Ata Anterior" do vereador:
//  1) Ler a ata (o documento anexado no Tramite)
//  2) Pedir retificacao (texto livre) ate a ata ser votada
//  3) Acompanhar os pedidos: aguardando / aceito / recusado
// =====================================================================

const dataBR = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');
const horaBR = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const tituloAta = (a) => (a ? (a.ementa || `Ata nº ${a.numero || ''}${a.ano ? '/' + a.ano : ''}`) : '');
const SIT = {
  PENDENTE: { rot: 'Aguardando análise', cor: '#b45309', fundo: '#fef3c7', ic: <Clock size={14} /> },
  ACEITO: { rot: 'Aceito', cor: '#15803d', fundo: '#dcfce7', ic: <Check size={14} /> },
  RECUSADO: { rot: 'Recusado', cor: '#b91c1c', fundo: '#fee2e2', ic: <X size={14} /> }
};

const CSS = `
.pav { max-width: 860px; margin: 0 auto; padding: 4px 2px 40px; color: #0f172a; }
.pav-cab { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
.pav-cab-ic { width: 52px; height: 52px; border-radius: 16px; background: #fef3c7; color: #b45309; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.pav-cab b { display: block; font-size: 1.35rem; font-weight: 800; }
.pav-cab small { display: block; color: #64748b; font-size: .9rem; margin-top: 2px; }
.pav-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 16px; margin-bottom: 12px; }
.pav-tit { font-weight: 800; font-size: 1.1rem; line-height: 1.3; overflow-wrap: anywhere; }
.pav-meta { color: #64748b; font-size: .88rem; margin-top: 4px; display: flex; flex-wrap: wrap; gap: 10px; }
.pav-chip { display: inline-flex; align-items: center; gap: 5px; font-size: .78rem; font-weight: 800; padding: 4px 10px; border-radius: 999px; }
.pav-passo { display: flex; align-items: center; gap: 10px; font-size: .78rem; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #475569; margin: 20px 0 10px; }
.pav-passo span { width: 24px; height: 24px; border-radius: 50%; background: #1d4ed8; color: #fff; display: inline-flex; align-items: center; justify-content: center; letter-spacing: 0; font-size: .8rem; }
.pav-bt { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 52px; padding: 0 18px; border-radius: 12px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; font: inherit; font-weight: 800; font-size: 1rem; cursor: pointer; }
.pav-bt[disabled] { opacity: .5; cursor: default; }
.pav-bt.azul { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.pav-bt.cheio { width: 100%; }
.pav-docs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.pav-ta { width: 100%; min-height: 140px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 12px; padding: 12px; font: inherit; font-size: 1.05rem; line-height: 1.5; resize: vertical; background: #fff; color: #0f172a; }
.pav-dica { color: #64748b; font-size: .88rem; margin: 0 0 8px; }
.pav-ped { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 10px; background: #f8fafc; }
.pav-ped p { margin: 8px 0 0; white-space: pre-wrap; line-height: 1.5; overflow-wrap: anywhere; }
.pav-resp { margin-top: 8px; padding: 8px 10px; border-left: 3px solid #94a3b8; background: #fff; border-radius: 6px; font-size: .92rem; }
.pav-vazio { background: #fff; border: 1px dashed #cbd5e1; border-radius: 14px; padding: 22px; text-align: center; color: #64748b; }
.pav-aviso { background: #f1f5f9; border-radius: 12px; padding: 12px; color: #334155; font-size: .95rem; }
.pav-outras { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.pav-outras button { border: 1px solid #cbd5e1; background: #fff; border-radius: 999px; padding: 8px 14px; font: inherit; font-weight: 700; cursor: pointer; color: #334155; }
.pav-outras button.ativo { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
`;

let sockAta = null;

export default function AtaVereador({ usuario }) {
  const [dados, setDados] = useState(null);
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState(null);
  const [docSel, setDocSel] = useState(0);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(() => {
    axios.get('/api/atas/minhas').then(r => setDados(r.data || { atas: [], pedidos: [] })).catch(() => setDados(d => d || { erro: true, atas: [], pedidos: [] }));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  // tempo real: o admin aceitou/recusou -> atualiza sozinho
  const camaraId = usuario && usuario.camaraId;
  useEffect(() => {
    if (!camaraId) return undefined;
    if (!sockAta) sockAta = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    const s = sockAta;
    const entrar = () => { s.emit('entrar-sala', `camara-${camaraId}`); carregar(); };
    if (s.connected) s.emit('entrar-sala', `camara-${camaraId}`);
    s.on('connect', entrar); s.on('atas-atualizado', carregar);
    const voltou = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', voltou);
    return () => { s.off('connect', entrar); s.off('atas-atualizado', carregar); document.removeEventListener('visibilitychange', voltou); };
  }, [camaraId, carregar]);

  const atas = (dados && dados.atas) || [];
  const ata = atas.find(a => a.id === sel) || atas[0] || null;
  const ataId = ata ? ata.id : null;
  useEffect(() => { setDocs(null); setDocSel(0); }, [ataId]);
  const meus = ((dados && dados.pedidos) || []).filter(p => ata && p.projetoId === ata.id);

  const lerAta = async () => {
    if (!ata) return;
    if (docs) { setDocs(null); return; } // PLENARIUM:LEITOR_PDF -- abre e fecha a ata aqui mesmo
    try {
      const r = await axios.get(`/api/projetos-camara/${ata.id}/documentos`);
      const l = [];
      if (r.data && r.data.temPrincipal) l.push({ k: 'p', t: 'Ata', url: `/api/projetos-camara/${ata.id}/principal` });
      ((r.data && r.data.anexos) || []).forEach(a => l.push({ k: a.id, t: a.nome || 'Documento', url: `/api/projetos-camara/anexo/${a.id}` }));
      setDocSel(0); setDocs(l);
    } catch (e) { toast.error('Não foi possível abrir a ata.'); }
  };

  const enviar = async () => {
    if (texto.trim().length < 5) { toast.error('Escreva o que precisa ser corrigido.'); return; }
    setEnviando(true);
    try {
      await axios.post(`/api/atas/${ata.id}/pedidos`, { texto: texto.trim() });
      setTexto(''); toast.success('Pedido enviado à Mesa.'); carregar();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível enviar.'); }
    setEnviando(false);
  };
  const cancelar = async (p) => {
    if (!window.confirm('Cancelar este pedido de retificação?')) return;
    try { await axios.delete(`/api/atas/pedidos/${p.id}`); toast.success('Pedido cancelado.'); carregar(); }
    catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível cancelar.'); }
  };

  return (
    <div className="pav">
      <style>{CSS}</style>
      <div className="pav-cab">
        <span className="pav-cab-ic"><BookOpen size={28} /></span>
        <span><b>Ata da Sessão Anterior</b><small>Leia a ata e, se precisar, peça retificação antes da votação.</small></span>
      </div>

      {!dados ? <div className="pav-vazio">Carregando…</div>
        : !ata ? <div className="pav-vazio">{dados.erro ? 'Não foi possível buscar a ata.' : 'Nenhuma ata disponível ainda. Quando a secretaria cadastrar a ata no Trâmite, ela aparece aqui.'}</div>
        : (
          <>
            {atas.length > 1 && (
              <div className="pav-outras">
                {atas.map(a => <button key={a.id} type="button" className={a.id === ata.id ? 'ativo' : ''} onClick={() => setSel(a.id)}>{a.numero ? `Ata ${a.numero}${a.ano ? '/' + a.ano : ''}` : dataBR(a.criadoEm)}</button>)}
              </div>
            )}

            <div className="pav-card">
              <div className="pav-tit">{tituloAta(ata)}</div>
              <div className="pav-meta">
                {ata.numero && <span>Nº {ata.numero}{ata.ano ? '/' + ata.ano : ''}</span>}
                {ata.sessao && <span>Votação: {ata.sessao.titulo} · {dataBR(ata.sessao.data)}</span>}
              </div>
              <div style={{ marginTop: 10 }}>
                {ata.aberta
                  ? <span className="pav-chip" style={{ color: '#15803d', background: '#dcfce7' }}><Check size={14} /> Aberta para pedidos de retificação</span>
                  : <span className="pav-chip" style={{ color: '#475569', background: '#f1f5f9' }}>Ata já votada · {ata.resultado === 'APROVADA' ? 'Aprovada' : 'Reprovada'}</span>}
              </div>

              <div className="pav-passo"><span>1</span> Ler a ata</div>
              <button type="button" className={`pav-bt cheio ${docs ? '' : 'azul'}`} onClick={lerAta}><FileText size={20} /> {docs ? 'Fechar a ata' : 'Ler a ata'}</button>
              {docs && docs.length > 1 && (
                <div className="pav-docs">
                  {docs.map((d, i) => <button key={d.k} type="button" className={`pav-bt ${i === docSel ? 'azul' : ''}`} onClick={() => setDocSel(i)}><FileText size={18} /> {d.t}</button>)}
                </div>
              )}
              {docs && (docs.length
                ? <LeitorPDF url={docs[Math.min(docSel, docs.length - 1)].url} titulo={docSel === 0 ? tituloAta(ata) : docs[docSel].t} />
                : <p className="pav-dica" style={{ marginTop: 10 }}>A ata está cadastrada, mas ainda sem documento anexado.</p>)}

              {ata.aberta && (
                <>
                  <div className="pav-passo"><span>2</span> Pedir retificação</div>
                  {ata.emVotacao
                    ? <div className="pav-aviso">A ata está em votação agora. Peça a retificação ao Presidente, no plenário.</div>
                    : (
                      <>
                        <p className="pav-dica">Diga o que está errado e como deve ficar. Ex.: “Na página 2, onde se lê ‘aprovado por 7 votos’, leia-se ‘aprovado por 8 votos’.”</p>
                        <textarea className="pav-ta" value={texto} maxLength={4000} onChange={e => setTexto(e.target.value)} placeholder="Escreva aqui o que deve ser corrigido…" />
                        <button type="button" className="pav-bt azul cheio" style={{ marginTop: 10 }} disabled={enviando || texto.trim().length < 5} onClick={enviar}><Send size={18} /> {enviando ? 'Enviando…' : 'Enviar pedido à Mesa'}</button>
                      </>
                    )}
                </>
              )}
            </div>

            <div className="pav-passo"><span>{ata.aberta ? 3 : 2}</span> Meus pedidos</div>
            {!meus.length ? <div className="pav-vazio">Você não fez pedidos de retificação para esta ata.</div>
              : meus.map(p => {
                const s = SIT[p.status] || SIT.PENDENTE;
                return (
                  <div key={p.id} className="pav-ped">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span className="pav-chip" style={{ color: s.cor, background: s.fundo }}>{s.ic} {s.rot}</span>
                      <small style={{ color: '#64748b' }}>enviado em {horaBR(p.criadoEm)}</small>
                      {p.status === 'PENDENTE' && ata.aberta && <button type="button" className="pav-bt" style={{ minHeight: 38, marginLeft: 'auto', fontSize: '.88rem' }} onClick={() => cancelar(p)}><Trash2 size={16} /> Cancelar</button>}
                    </div>
                    <p>{p.texto}</p>
                    {p.status === 'ACEITO' && p.textoAceito && p.textoAceito !== p.texto && <div className="pav-resp"><b>Como entrou na retificação:</b> {p.textoAceito}</div>}
                    {p.resposta && <div className="pav-resp"><b>Resposta da Mesa:</b> {p.resposta}</div>}
                  </div>
                );
              })}
          </>
        )}
    </div>
  );
}
